import api from '../../lib/api';
import type {
  CreateStudyPayload,
  RiskStudy,
  StudyActivity,
  StudyStatus,
  StudySummary,
} from './types';

export const fetchRiskStudies = async (params?: {
  orgUnitId?: string;
  status?: StudyStatus;
}): Promise<RiskStudy[]> => {
  const { data } = await api.get<RiskStudy[]>('risk-studies', { params });
  return data;
};

export const fetchRiskStudy = async (id: string): Promise<RiskStudy> => {
  const { data } = await api.get<RiskStudy>(`risk-studies/${id}`);
  return data;
};

export const createRiskStudy = async (payload: CreateStudyPayload): Promise<RiskStudy> => {
  const { data } = await api.post<RiskStudy>('risk-studies', payload);
  return data;
};

export const updateRiskStudy = async (
  id: string,
  payload: Partial<CreateStudyPayload>,
): Promise<RiskStudy> => {
  const { data } = await api.put<RiskStudy>(`risk-studies/${id}`, payload);
  return data;
};

export const setStudyStatus = async (id: string, status: StudyStatus): Promise<RiskStudy> => {
  const { data } = await api.patch<RiskStudy>(`risk-studies/${id}/status`, { status });
  return data;
};

export const duplicateRiskStudy = async (
  id: string,
  options?: { newOrgUnitId?: string; codeSuffix?: string },
): Promise<RiskStudy> => {
  const { data } = await api.post<RiskStudy>(`risk-studies/${id}/duplicate`, options ?? {});
  return data;
};

export const fetchStudySummary = async (id: string): Promise<StudySummary> => {
  const { data } = await api.get<StudySummary>(`risk-studies/${id}/summary`);
  return data;
};

export const deleteRiskStudy = async (id: string): Promise<void> => {
  await api.delete(`risk-studies/${id}`);
};

export const fetchStudyActivities = async (studyId: string): Promise<StudyActivity[]> => {
  const { data } = await api.get<StudyActivity[]>(`risk-studies/${studyId}/activities`);
  return data;
};

export const createStudyActivity = async (
  studyId: string,
  payload: { parentId?: string | null; name: string; sortOrder?: number },
): Promise<StudyActivity> => {
  const { data } = await api.post<StudyActivity>(`risk-studies/${studyId}/activities`, payload);
  return data;
};

export const updateStudyActivity = async (
  studyId: string,
  activityId: string,
  payload: { name?: string; sortOrder?: number; parentId?: string | null },
): Promise<StudyActivity> => {
  const { data } = await api.put<StudyActivity>(
    `risk-studies/${studyId}/activities/${activityId}`,
    payload,
  );
  return data;
};

export const deleteStudyActivity = async (studyId: string, activityId: string): Promise<void> => {
  await api.delete(`risk-studies/${studyId}/activities/${activityId}`);
};
